import { load } from "@loaders.gl/core";
import { PLYLoader as PLY } from "@loaders.gl/ply";
import {
  Box3,
  LoadingManager,
  Points,
  Vector3,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  IPointCloudLoader,
  IPointCloudLoadParams,
  IPointCloudTileGeometry,
} from "./interface";
import { getZipResponse } from "./utils";
import { DndProvider } from "react-dnd";

export class PLYLoader implements IPointCloudLoader {
  async load(
    params: IPointCloudLoadParams,
    abortController: AbortController
  ): Promise<IPointCloudTileGeometry> {
    const response = await getZipResponse(
      params.url as string,
      abortController
    );
    if (response instanceof Blob) {
      // PLY
      const data = await load(response, PLY, {
        fetch: { credentials: "include", signal: abortController.signal },
      });
      const bbox = new Box3().setFromArray([
        ...data.header.boundingBox[0],
        ...data.header.boundingBox[1],
      ]);
      const center = new Vector3();
      bbox.getCenter(center);
      const offset = center.negate();
      bbox.translate(offset);
      const vertices = new Float32Array(data.attributes.POSITION.value.length); // xyz
      for (let i = 0; i < data.attributes.POSITION.value.length; i += 3) {
        vertices[i] = data.attributes.POSITION.value[i] + offset.x;
        vertices[i + 1] = data.attributes.POSITION.value[i + 1] + offset.y;
        vertices[i + 2] = data.attributes.POSITION.value[i + 2] + offset.z;
      }

      // Debug PLY data attributes
      
      // Check color attribute
      if (!data.attributes.COLOR_0 || !data.attributes.COLOR_0.value) {
        console.error('No COLOR_0 attribute found in PLY data');
        // Create default white colors
        const pointCount = vertices.length / 3;
        const color = new Float32Array(pointCount * 3);
        for (let i = 0; i < color.length; i += 3) {
          color[i] = 1.0;     // Red
          color[i + 1] = 1.0; // Green  
          color[i + 2] = 1.0; // Blue
        }
        
        return {
          vertices,
          color,
          offset,
          bbox,
        };
      }

      const color = new Float32Array(data.attributes.COLOR_0.value.length); // データからR, G, Bを抽出
      for (let i = 0; i < data.attributes.COLOR_0.value.length; i++) {
        color[i] = data.attributes.COLOR_0.value[i] / 255; // 0-255で来るRGBを0-1に変換
      }
      return {
        vertices,
        color,
        offset,
        bbox,
      };
    } else {
      // GLTF with multiple files
      console.log('Processing GLTF files:', Object.keys(response));
      
      // Cache for URL mappings to prevent duplicates
      const urlCache = new Map<string, string>();
      
      const manager = new LoadingManager();
      manager.setURLModifier((url) => {
        // Check cache first
        if (urlCache.has(url)) {
          return urlCache.get(url)!;
        }
        
        if (response[url]) {
          const objectUrl = window.URL.createObjectURL(response[url]);
          // Cache the mapping
          urlCache.set(url, objectUrl);
          return objectUrl;
        }
        console.warn(`File not found in response: ${url}`);
        return url;
      });
      
      const loader = new GLTFLoader(manager);
      let gltfFile: string;
      
      // Find GLTF file - improved detection
      Object.keys(response).forEach((key) => {
        const ext = key.split(".").reverse()[0].toLowerCase();

        if (ext === "gltf") {
          gltfFile = key;
          console.log(`Found GLTF file: ${gltfFile}`);
        }
      });
      
      if (!gltfFile) {
        console.error('No GLTF file found in ZIP contents');
        throw new Error('No GLTF file found in ZIP');
      }

      const load = new Promise<IPointCloudTileGeometry>((resolve, reject) => {
        console.log(`Loading GLTF file: ${gltfFile}`);
        
        loader.load(
          gltfFile, 
          async (gltf) => {
            console.log('GLTF loaded successfully - processing point cloud data');
            
            let pointcloud: Points;
            for (const [key, value] of gltf.parser.associations) {
              if (key instanceof Points) {
                pointcloud = key as Points;
                console.log('Found Points object in GLTF');
                break;
              }
            }
            
            if (!pointcloud) {
              console.error('No Points object found in GLTF');
              reject(new Error('No point cloud data found in GLTF'));
              return;
            }
          // console.log(pointcloud)
          if (!pointcloud.geometry.boundingBox) {
            pointcloud.geometry.computeBoundingBox();
          }
          const bbox = pointcloud.geometry.boundingBox.clone();
          const center = new Vector3();
          bbox.getCenter(center);
          const offset = center.negate();
          bbox.translate(offset);
          const posCount = pointcloud.geometry.attributes.position.count;
          const vertices = new Float32Array(posCount * 3); // xyz

          for (let i = 0; i < posCount; ++i) {
            vertices[3 * i]     = pointcloud.geometry.attributes.position.getX(i) + offset.x;
            vertices[3 * i + 1] = pointcloud.geometry.attributes.position.getY(i) + offset.y;
            vertices[3 * i + 2] = pointcloud.geometry.attributes.position.getZ(i) + offset.z;
          }

          // Debug geometry attributes
          console.log('GLTF geometry attributes:', Object.keys(pointcloud.geometry.attributes));
          console.log('Position attribute:', {
            count: pointcloud.geometry.attributes.position.count,
            itemSize: pointcloud.geometry.attributes.position.itemSize
          });
          
          // Check color attribute
          if (!pointcloud.geometry.attributes.color) {
            console.error('No color attribute found in GLTF geometry');
            // Create default white colors
            const pointCount = vertices.length / 3;
            const color = new Float32Array(pointCount * 3);
            for (let i = 0; i < color.length; i += 3) {
              color[i] = 1.0;     // Red
              color[i + 1] = 1.0; // Green  
              color[i + 2] = 1.0; // Blue
            }
            console.log('Created default white colors for', pointCount, 'points');
            
            resolve({
              vertices,
              color,
              offset,
              bbox,
            });
            return;
          }

          console.log('Color attribute found:', {
            count: pointcloud.geometry.attributes.color.count,
            itemSize: pointcloud.geometry.attributes.color.itemSize,
            normalized: pointcloud.geometry.attributes.color.normalized,
            arrayLength: pointcloud.geometry.attributes.color.array.length
          });

          // Debug geometry attributes
          console.log('GLTF geometry attributes:', Object.keys(pointcloud.geometry.attributes));
          console.log('Position attribute:', {
            count: pointcloud.geometry.attributes.position.count,
            itemSize: pointcloud.geometry.attributes.position.itemSize
          });
          
          // Check color attribute
          if (!pointcloud.geometry.attributes.color) {
            console.error('No color attribute found in GLTF geometry');
            // Create default white colors
            const pointCount = vertices.length / 3;
            const color = new Float32Array(pointCount * 3);
            for (let i = 0; i < color.length; i += 3) {
              color[i] = 1.0;     // Red
              color[i + 1] = 1.0; // Green  
              color[i + 2] = 1.0; // Blue
            }
            console.log('Created default white colors for', pointCount, 'points');
            
            resolve({
              vertices,
              color,
              offset,
              bbox,
            });
            return;
          }

          console.log('Color attribute found:', {
            count: pointcloud.geometry.attributes.color.count,
            itemSize: pointcloud.geometry.attributes.color.itemSize,
            normalized: pointcloud.geometry.attributes.color.normalized,
            arrayLength: pointcloud.geometry.attributes.color.array.length
          });

          const color = new Float32Array(
            pointcloud.geometry.attributes.color.count * 3
          ); // rgb

          // const divideFactor = 
          //  pointcloud.geometry.attributes.color.normalized
          //   ? 255
          //   : 1;
          

          console.log(pointcloud.geometry.attributes.color);
          for (let i = 0; i < pointcloud.geometry.attributes.color.count; ++i) {
            if (i < 5) {
              console.log(`origin value  ${i}: RGB(${pointcloud.geometry.attributes.color.getX(i)}, ${pointcloud.geometry.attributes.color.getY(i)}, ${pointcloud.geometry.attributes.color.getZ(i)})`);
            }
            
            const r = pointcloud.geometry.attributes.color.getX(i) ;
            const g = pointcloud.geometry.attributes.color.getY(i) ;
            const b = pointcloud.geometry.attributes.color.getZ(i) ;
            
            color[3 * i] = r;
            color[3 * i + 1] = g;
            color[3 * i + 2] = b;
            
            // Debug first few colors
            if (i < 5) {
              console.log(`Color ${i}: RGB(${r}, ${g}, ${b})`);
              
              console.log(`Color ${i}: RGB(${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)})`);
            }
          }
          
          console.log('Color processing completed for', pointcloud.geometry.attributes.color.count, 'points');

          resolve({
            vertices,
            color,
            offset,
            bbox,
          });
        }, 
        undefined, // No progress callback needed
        (error) => {
          console.error('GLTF loading failed:', error);
          reject(error);
        });
      });
      
      try {
        const result = await load;
        
        // Cleanup object URLs to prevent memory leaks
        console.log(`Cleaning up ${urlCache.size} cached URLs`);
        urlCache.forEach((objectUrl, originalUrl) => {
          if (objectUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(objectUrl);
          }
        });
        urlCache.clear();
        
        return result;
      } catch (error) {
        console.error('Error processing GLTF:', error);
        
        // Cleanup on error
        console.log(`Cleaning up ${urlCache.size} cached URLs after error`);
        urlCache.forEach((objectUrl, originalUrl) => {
          try {
            if (objectUrl.startsWith('blob:')) {
              window.URL.revokeObjectURL(objectUrl);
            }
          } catch (e) {
            // Ignore cleanup errors
          }
        });
        urlCache.clear();
        
        throw error;
      }
    }
  }
}
